import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import FinanceV2Layout from "./FinanceV2Layout";
import { NoteState } from "../../context/notes/NoteState";
import FinanceV2Services from "../../UserServices/FinanceV2Services";
import "./FinanceV2.css";

export default function FinanceV2BankReconcile() {
  const { state } = useContext(NoteState);
  const { branch_id } = state;
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [asOf, setAsOf] = useState(new Date().toISOString().split("T")[0]);
  const [statementBalance, setStatementBalance] = useState("");
  const [clearedDate, setClearedDate] = useState(new Date().toISOString().split("T")[0]);
  const [summary, setSummary] = useState(null);
  const [lines, setLines] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    FinanceV2Services.getAccountsList(branch_id)
      .then((res) => {
        const asset = (res.data?.data || []).filter(
          (a) => a.account_type?.code === "asset" && /bank|cash/i.test(a.name + a.code)
        );
        setAccounts(asset.length ? asset : res.data?.data || []);
      })
      .catch(() => toast.error("Could not load accounts"));
  }, [branch_id]);

  const load = async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const [sumRes, uncRes] = await Promise.all([
        FinanceV2Services.getBankSummary(accountId, {
          as_of: asOf,
          statement_balance: statementBalance || undefined,
        }),
        FinanceV2Services.getBankUncleared(accountId, { to: asOf }),
      ]);
      setSummary(sumRes.data?.data);
      setLines(uncRes.data?.data || []);
      setSelected([]);
    } catch (e) {
      toast.error("Could not load reconciliation");
    } finally {
      setLoading(false);
    }
  };

  const toggleLine = (lineId) => {
    setSelected((prev) =>
      prev.includes(lineId) ? prev.filter((id) => id !== lineId) : [...prev, lineId]
    );
  };

  const markCleared = async () => {
    if (!selected.length) {
      toast.info("Select lines to clear");
      return;
    }
    try {
      await FinanceV2Services.clearBankLines({
        line_ids: selected,
        cleared_date: clearedDate,
      });
      toast.success(`Cleared ${selected.length} line(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Clear failed");
    }
  };

  return (
    <FinanceV2Layout>
      <div className="container-fluid px-0 py-2">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Bank reconciliation</h4>
          <Link to="/finance-v2/dashboard" className="btn btn-sm btn-outline-secondary">
            Dashboard
          </Link>
        </div>

        <div className="fv2-card mb-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label small">Bank / cash account</label>
              <select
                className="form-select form-select-sm"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
              >
                <option value="">— Select —</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} — {a.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <label className="form-label small">As of</label>
              <input
                type="date"
                className="form-control form-control-sm"
                value={asOf}
                onChange={(e) => setAsOf(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Statement balance</label>
              <input
                type="number"
                className="form-control form-control-sm"
                value={statementBalance}
                onChange={(e) => setStatementBalance(e.target.value)}
                placeholder="Optional"
              />
            </div>
            <div className="col-auto">
              <button type="button" className="btn btn-sm btn-success" disabled={loading} onClick={load}>
                Load
              </button>
            </div>
          </div>
        </div>

        {summary && (
          <div className="row g-3 mb-3">
            <div className="col-md-3">
              <div className="fv2-card">
                <div className="text-muted small">Book balance</div>
                <div className="fv2-metric">{summary.book_balance}</div>
              </div>
            </div>
            <div className="col-md-3">
              <div className="fv2-card">
                <div className="text-muted small">Uncleared ({summary.uncleared_count})</div>
                <div className="fv2-metric">{summary.uncleared_net}</div>
              </div>
            </div>
            {summary.difference != null && (
              <div className="col-md-3">
                <div className="fv2-card">
                  <div className="text-muted small">Difference</div>
                  <div className={`fv2-metric ${summary.difference !== 0 ? "text-danger" : ""}`}>
                    {summary.difference}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="d-flex gap-2 mb-2 align-items-center">
          <label className="small mb-0">Cleared date</label>
          <input
            type="date"
            className="form-control form-control-sm"
            style={{ width: 160 }}
            value={clearedDate}
            onChange={(e) => setClearedDate(e.target.value)}
          />
          <button type="button" className="btn btn-sm btn-primary" onClick={markCleared}>
            Mark selected cleared
          </button>
        </div>

        <div className="fv2-table-wrap">
          <table className="table table-sm">
            <thead>
              <tr>
                <th></th>
                <th>Date</th>
                <th>Reference</th>
                <th>Description</th>
                <th className="text-end">Debit</th>
                <th className="text-end">Credit</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.line_id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.includes(l.line_id)}
                      onChange={() => toggleLine(l.line_id)}
                    />
                  </td>
                  <td>{l.entry_date}</td>
                  <td>{l.reference}</td>
                  <td>{l.description}</td>
                  <td className="text-end">{l.debit}</td>
                  <td className="text-end">{l.credit}</td>
                </tr>
              ))}
              {!lines.length && (
                <tr>
                  <td colSpan={6} className="text-muted text-center">
                    Select account and load uncleared lines
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </FinanceV2Layout>
  );
}
